export const normalizeRole = (r) => {
  if (!r) return null
  if (typeof r === 'string') return r.trim().toLowerCase()
  return String(r.slug || r.name || r.code || '').trim().toLowerCase() || null
}

export const getRoleSlugs = (user) => {
  if (!user) return []
  const roles = []
  if (Array.isArray(user.roles)) roles.push(...user.roles)
  if (user.role) roles.push(user.role)
  if (user.rol) roles.push(user.rol)
  return roles.map(normalizeRole).filter(Boolean)
}

export const hasRole = (user, role) =>
  getRoleSlugs(user).includes(String(role).toLowerCase())

export const hasAnyRole = (user, roles) =>
  roles.some((r) => hasRole(user, r))

export const isPrivileged = (user) =>
  hasAnyRole(user, ['gestor', 'admin', 'encargado', 'administrativo'])

export const isTechnicianOnly = (user) =>
  hasRole(user, 'tecnico') && !isPrivileged(user)

// Técnico "jefe de zona": rol tecnico + flag puede_gestionar_tecnicos.
// Espejo de User::isTechLead() en el backend.
export const isTechLead = (user) =>
  hasRole(user, 'tecnico') && !!(user?.puedeGestionarTecnicos ?? user?.puede_gestionar_tecnicos)

// ¿Puede asignar/desasignar incidencias a OTROS técnicos? El alcance (zona) lo
// valida el backend: el técnico jefe solo manda en sus zonas.
export const canManageTechnicians = (user) =>
  isPrivileged(user) || hasRole(user, 'soporte') || isTechLead(user)

// ¿Puede ver las rutas GPS (de técnicos y de cualquiera con seguimiento)?
// Mismo criterio que /logistics/gps; el alcance lo recorta el backend: quien va
// por zonas sólo ve las suyas, y nunca las de un administrador.
export const canSeeTechnicianRoutes = (user) =>
  isPrivileged(user) || isTechLead(user)

// Capacidades sobre incidencias (espejo de la autorización del backend).
// Mantener sincronizado con IncidentController.
export const canCreateIncident = (user) =>
  hasAnyRole(user, ['admin', 'gestor', 'encargado', 'administrativo', 'tecnico', 'operador'])

export const canResolveIncident = (user) =>
  hasAnyRole(user, ['admin', 'gestor', 'encargado', 'administrativo', 'tecnico', 'operador'])

export const canSelfAssignIncident = (user) =>
  hasAnyRole(user, ['tecnico', 'operador'])

// Editar una incidencia YA CERRADA: reservado al administrador. El resto de
// roles con permiso de edición (gestor, encargado, administrativo) solo pueden
// editar mientras la incidencia sigue abierta.
export const canEditClosedIncident = (user) => hasRole(user, 'admin')

// Recurrir / apelar: técnicos (en su zona) u operadores (en su salón); el alcance
// lo valida el backend. La acción solo aparece sobre incidencias asignadas al usuario.
export const canAppealIncident = (user) =>
  hasAnyRole(user, ['tecnico', 'operador'])

// Adjuntar fotos a una incidencia existente. El alcance (zona / salón) lo valida
// el backend; el front solo muestra el botón a los roles habilitados.
export const canAddAttachments = (user) =>
  hasAnyRole(user, ['tecnico', 'operador', 'gestor', 'admin', 'encargado', 'administrativo', 'soporte'])

// Quitar una foto de una incidencia: quien puede adjuntar, pero solo
// gestor/admin/soporte puede borrar las fotos de otros. Misma regla en el backend.
export const canDeleteAttachment = (user, photo) =>
  canAddAttachments(user) &&
  (hasAnyRole(user, ['gestor', 'admin', 'soporte']) ||
    (photo?.created_by != null && Number(photo.created_by) === Number(user?.id)))
